import React, { useContext, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as ls from 'local-storage'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown, faAngleRight, faAngleLeft } from '@fortawesome/free-solid-svg-icons'
import LoadingButton from '@mui/lab/LoadingButton'
import { Button, Collapse } from '@mui/material'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import { API } from '../../providers/API'
import { GlobalStateContext } from '../../providers/GlobalStateProvider'
import { CollapseMenuButton, StyledMenu } from './Slideout.styles'
import logo from '../../media/agro-logo.svg'
import logoNoText from '../../media/agro-svg-no-text.svg'
import {
  IconAccount,
  IconCreateField,
  IconInvoices,
  IconLogout,
  IconMyFields,
  IconMyMixes,
  // IconQuestion,
  IconWeatherData,
  IconPrice,
  IconSoilAnalysis,
} from '../../icons/index'
import { IconSateliteData } from '../../icons/IconSateliteData'

type logoutUserProp = {
  // eslint-disable-next-line no-unused-vars
  (redirect?: boolean) : void
}

export const useOnClickOutside = (ref: any, handler: any) => {
  useEffect(() => {
    const listener = (event: any) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return
      }
      handler(event)
    }
    document.addEventListener('mousedown', listener)
    return () => {
      document.removeEventListener('mousedown', listener)
    }
  },
  [ref, handler])
}

export const Slideout: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [collapseMenu, setCollapseMenu] = useState(false)
  const [collapseSatMenu, setCollapseSatMenu] = useState(false)
  const { t } = useTranslation()

  const {
    isNavCollapsed,
    setIsNavCollapsed,
    slideoutOpen,
    setSlideoutOpen, userInfo, userAccess, setUserAccess, setUserInfo, setAuthError,
  } = useContext(GlobalStateContext)

  const logoutUser: logoutUserProp = (redirect = false) => {
    if (redirect) {
      setUserAccess(0)
      ls.set('userAccess', 0)
      setAuthError(false)
    }
    ls.set('loginCredentials', '')
    ls.set('userInfo', '')
    setUserInfo({})
    setIsLoggingOut(false)
  }

  useEffect(() => {
    // @ts-ignore
    if (location.pathname !== '/createMix' && location.pathname !== '/mixes') {
      setCollapseMenu(false)
    }
  }, [location.pathname])

  useEffect(() => {
    // get and set user info and access level on page load to prevent unauthorized access
    if (userInfo.name) {
      API.get(`/userData/${userInfo.id}`, { headers: { Authorization: `Bearer ${ls.get('loginCredentials')}` } }).then((response) => {
        ls.set('userAccess', response.data.access)
        setUserAccess(response.data.access)
        setUserInfo(response.data)
        ls.set('userInfo', response.data)
      })
    }
  }, [])

  API.interceptors.response.use((response) => response, (error) => {
    if (error.response.status === 401 && error.config.method !== 'get' && error.response.data.message !== 'Email or password are not correct!') {
      setSlideoutOpen(false)
      setAuthError(true)
      logoutUser()
    } else if (error.response.status === 401 && error.config.method === 'get') {
      logoutUser(true)
    }

    return Promise.reject(error)
  })

  if (!userInfo.name) {
    return null
  }

  return (
    <StyledMenu open={slideoutOpen} isNavCollapsed={isNavCollapsed}>
      <img src={isNavCollapsed ? logoNoText : logo} alt="" className="logo" />
      <CollapseMenuButton
        type="button"
        onClick={() => {
          setIsNavCollapsed((prev) => !prev)
          setCollapseSatMenu(false)
          setCollapseMenu(false)
        }}
      >
        <FontAwesomeIcon icon={isNavCollapsed ? faAngleRight : faAngleLeft} />
      </CollapseMenuButton>
      {userAccess !== 0 && (
        <>
          <ul>
            <li>
              <Button
                type="button"
                variant="contained"
                fullWidth
                className={location.pathname === '/fields' || location.pathname === '/' ? 'active' : ''}
                onClick={() => {
                  setSlideoutOpen(false)
                  history.push('/fields')
                }}
                title={isNavCollapsed ? t('navigation.myFields') : undefined}
              >
                <IconMyFields />
                {' '}
                <span className="menuText">{t('navigation.myFields')}</span>
              </Button>
            </li>
            <li>
              <Button
                type="button"
                variant="contained"
                fullWidth
                className={location.pathname === '/createField' ? 'active' : ''}
                onClick={() => {
                  setSlideoutOpen(false)
                  history.push('/createField')
                }}
                title={isNavCollapsed ? t('navigation.createField') : undefined}
              >
                <IconCreateField />
                {' '}
                <span className="menuText">{t('navigation.createField')}</span>
              </Button>
            </li>
            <li>
              <Button
                type="button"
                variant="contained"
                fullWidth
                className={location.pathname === '/fieldZones' ? 'active' : ''}
                onClick={() => {
                  setSlideoutOpen(false)
                  history.push('/fieldZones')
                }}
                title={isNavCollapsed ? t('navigation.myZones') : undefined}
              >
                <IconMyFields />
                {' '}
                <span className="menuText">{t('navigation.myZones')}</span>
              </Button>
            </li>
            {/* <li>
              <Button
                type="button"
                variant="contained"
                fullWidth
                className={location.pathname === '/fieldsData' ? 'active' : ''}
                onClick={() => {
                  setSlideoutOpen(false)
                  history.push('/fieldsData')
                }}
                  title={isNavCollapsed ? t('navigation.fieldsDataOverview') : undefined}
              >
                <IconMyFields />
                {' '}
                <span className="menuText">{t('navigation.fieldsDataOverview')}</span>
              </Button>
            </li> */}
            <li>
              <Button
                type="button"
                variant="contained"
                fullWidth
                className={location.pathname === '/mixes' || location.pathname === '/createMix' ? 'active' : ''}
                onClick={() => {
                  setCollapseMenu(!collapseMenu)
                }}
                endIcon={<FontAwesomeIcon icon={collapseMenu ? faAngleDown : faAngleRight} />}
                title={isNavCollapsed ? t('navigation.mixes') : undefined}
              >
                <IconMyMixes size="34" />
                {' '}
                <span className="menuText">{t('navigation.mixes')}</span>
              </Button>
              <Collapse in={collapseMenu} timeout="auto" unmountOnExit>
                <List>
                  <ListItem disablePadding>
                    <ListItemButton
                      className={location.pathname === '/createMix' && location.search === '?mixType=ai' ? 'active' : ''}
                      onClick={() => {
                        setSlideoutOpen(false)
                        if (isNavCollapsed) {
                          setCollapseMenu(false)
                        }
                        history.push('/createMix?mixType=ai')
                      }}
                      title={isNavCollapsed ? t('navigation.aiMix') : undefined}
                    >
                      <span className="css-nodkat" /> {t('navigation.aiMix')}
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      className={location.pathname === '/createMix' && location.search === '?mixType=manual' ? 'active' : ''}
                      onClick={() => {
                        setSlideoutOpen(false)
                        if (isNavCollapsed) {
                          setCollapseMenu(false)
                        }
                        history.push('/createMix?mixType=manual')
                      }}
                      title={isNavCollapsed ? t('navigation.mix') : undefined}
                    >
                      <span className="css-nodkat" /> {t('navigation.mix')}
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      className={location.pathname === '/mixes' ? 'active' : ''}
                      onClick={() => {
                        setSlideoutOpen(false)
                        if (isNavCollapsed) {
                          setCollapseMenu(false)
                        }
                        history.push('/mixes')
                      }}
                      title={isNavCollapsed ? t('navigation.myMixes') : undefined}
                    >
                      <span className="css-nodkat" /> {t('navigation.myMixes')}
                    </ListItemButton>
                  </ListItem>
                </List>
                {/* <Button
                  type="button"
                  variant="contained"
                  fullWidth
                  className={location.pathname === '/mixes' ? 'active' : ''}
                  onClick={() => {
                    setSlideoutOpen(false)
                    history.push('/mixes')
                  }}
                    title={isNavCollapsed ? t('navigation.myMixes') : undefined}
                >
                  <IconMyMixes size="34" />
                  {' '}
                  <span className="menuText">{t('navigation.myMixes')}</span>
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  fullWidth
                  className={location.pathname === '/mixes' ? 'active' : ''}
                  onClick={() => {
                    setSlideoutOpen(false)
                    history.push('/mixes')
                  }}
                    title={isNavCollapsed ? t('navigation.myMixes') : undefined}
                >
                  <IconMyMixes size="34" />
                  {' '}
                  <span className="menuText">{t('navigation.myMixes')}</span>
                </Button> */}
              </Collapse>
            </li>
            <li>
              <Button
                type="button"
                variant="contained"
                fullWidth
                className={location.pathname === '/weatherListing' || location.pathname === '/weatherField' ? 'active' : ''}
                onClick={() => {
                  setSlideoutOpen(false)
                  history.push('/weatherListing')
                }}
                title={isNavCollapsed ? t('navigation.meteoData') : undefined}
              >
                <IconWeatherData />
                {' '}
                <span className="menuText">{t('navigation.meteoData')}</span>
              </Button>
            </li>
            <li>
              <Button
                type="button"
                variant="contained"
                fullWidth
                className={location.pathname === '/soilAnalysisListing' ? 'active' : ''}
                onClick={() => {
                  setSlideoutOpen(false)
                  history.push('/soilAnalysisListing')
                }}
                title={isNavCollapsed ? t('navigation.soilAnalysis') : undefined}
              >
                <IconSoilAnalysis />
                {' '}
                <span className="menuText">{t('navigation.soilAnalysis')}</span>
              </Button>
            </li>
            <li>
              <Button
                type="button"
                variant="contained"
                fullWidth
                className={location.pathname === '/ndviFieldsListing' || location.pathname === '/ndviFieldsCompare' ? 'active' : ''}
                onClick={() => {
                  setCollapseSatMenu(!collapseSatMenu)
                }}
                endIcon={<FontAwesomeIcon icon={collapseSatMenu ? faAngleDown : faAngleRight} />}
                title={isNavCollapsed ? t('navigation.sateliteData') : undefined}
              >
                <IconSateliteData size="34" />
                {' '}
                <span className="menuText">{t('navigation.sateliteData')}</span>
              </Button>
              <Collapse in={collapseSatMenu} timeout="auto" unmountOnExit>
                <List>
                  <ListItem disablePadding>
                    <ListItemButton
                      className={location.pathname === '/ndviFieldsListing' ? 'active' : ''}
                      onClick={() => {
                        setSlideoutOpen(false)
                        if (isNavCollapsed) {
                          setCollapseSatMenu(false)
                        }
                        history.push('/ndviFieldsListing')
                      }}
                      title={isNavCollapsed ? t('navigation.ndviEvi') : undefined}
                    >
                      <span className="css-nodkat" /> {t('navigation.ndviEvi')}
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      className={location.pathname === '/ndviFieldsCompare' ? 'active' : ''}
                      onClick={() => {
                        setSlideoutOpen(false)
                        if (isNavCollapsed) {
                          setCollapseSatMenu(false)
                        }
                        history.push('/ndviFieldsCompare')
                      }}
                      title={isNavCollapsed ? t('navigation.ndviCompare') : undefined}
                    >
                      <span className="css-nodkat" /> {t('navigation.ndviCompare')}
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      className={location.pathname === '/sateliteSoilHistory' ? 'active' : ''}
                      onClick={() => {
                        setSlideoutOpen(false)
                        if (isNavCollapsed) {
                          setCollapseSatMenu(false)
                        }
                        history.push('/sateliteSoilHistory')
                      }}
                      title={isNavCollapsed ? t('navigation.soilHistory') : undefined}
                    >
                      <span className="css-nodkat" /> {t('navigation.soilHistory')}
                    </ListItemButton>
                  </ListItem>
                </List>
              </Collapse>
            </li>
          </ul>
          <h3>{t('general.profile')}</h3>
          <ul>
            <li>
              <Button
                type="button"
                variant="contained"
                fullWidth
                className={location.pathname === '/account' ? 'active' : ''}
                onClick={() => {
                  setSlideoutOpen(false)
                  history.push('/account/profile')
                }}
                title={isNavCollapsed ? t('navigation.myData') : undefined}
              >
                <IconAccount />
                {' '}
                <span className="menuText">{t('navigation.myData')}</span>
              </Button>
            </li>
            <li>
              <Button
                type="button"
                variant="contained"
                fullWidth
                className={location.pathname === '/carbonEnrollment' ? 'active' : ''}
                onClick={() => {
                  setSlideoutOpen(false)
                  history.push('/carbonEnrollment')
                }}
                title={isNavCollapsed ? t('navigation.carbonEnrollment') : undefined}
              >
                <IconAccount />
                {' '}
                <span className="menuText">{t('navigation.carbonEnrollment')}</span>
              </Button>
            </li>
            <li>
              <Button
                type="button"
                variant="contained"
                fullWidth
                className={location.pathname === '/account' ? 'active' : ''}
                onClick={() => {
                  setSlideoutOpen(false)
                  history.push('/account/invoices')
                }}
                title={isNavCollapsed ? t('navigation.invoices') : undefined}
              >
                <IconInvoices />
                {' '}
                <span className="menuText">{t('navigation.invoices')}</span>
              </Button>
            </li>
            <li>
              <Button
                type="button"
                variant="contained"
                fullWidth
                className={location.pathname === '/tokenPackages' ? 'active' : ''}
                onClick={() => {
                  setSlideoutOpen(false)
                  history.push('/tokenPackages')
                }}
                title={isNavCollapsed ? t('navigation.tokenPackages') : undefined}
              >
                <IconPrice />
                {' '}
                <span className="menuText">{t('navigation.tokenPackages')}</span>
              </Button>
            </li>
            <li>
              <LoadingButton
                type="button"
                variant="contained"
                fullWidth
                onClick={() => {
                  setIsLoggingOut(true)
                  API.post('/logout', {}, { headers: { Authorization: `Bearer ${ls.get('loginCredentials')}` } }).then(() => {
                    logoutUser(true)
                  })
                }}
                loading={isLoggingOut}
                loadingPosition="start"
                startIcon={null}
                title={isNavCollapsed ? t('navigation.logout') : undefined}
              >
                {!isLoggingOut && <IconLogout />}
                {' '}
                <span className="menuText">{t('navigation.logout')}</span>
              </LoadingButton>
            </li>
          </ul>
        </>
      )}
    </StyledMenu>
  )
}
